export const DING_THROTTLE_MS = 1200;

function normalizeIdentity(value) {
  const normalized = String(value || '').trim();
  return normalized || null;
}

export function buildUnreadMap(rawConversations) {
  const map = {};
  (rawConversations || []).forEach((item) => {
    const convId = item?.conversation?.id;
    if (!convId) return;
    map[convId] = Number.isFinite(item?.unreadCount) ? item.unreadCount : 0;
  });
  return map;
}

export function syncUnreadCounts(
  previous,
  incomingMap,
  selectedConversationId,
  recentlyMarkedReadIds
) {
  const next = {};
  Object.keys(incomingMap || {}).forEach((conversationId) => {
    next[conversationId] = Number.isFinite(incomingMap[conversationId])
      ? incomingMap[conversationId]
      : 0;
  });

  if (
    selectedConversationId
    && Object.prototype.hasOwnProperty.call(next, selectedConversationId)
  ) {
    next[selectedConversationId] = 0;
  }

  // Preserve PREVIOUS local state for conversations whose mark-read mutation
  // is currently being reconciled. Two cases this defends against:
  //
  // 1. Stale list response: the user just marked-read; previous local is 0;
  //    server's response was computed against a not-yet-propagated lastReadAt
  //    and reports an unread count > 0. We keep previous = 0.
  //
  // 2. Subscription-bumped real unread: the user marked-read; navigated away;
  //    a real new message arrived via ON_NEW_MESSAGE which incremented previous
  //    local to 1; a stale or fresh list response then arrives. We keep
  //    previous = 1, NOT force 0 (which would silently hide a real unread).
  //
  // Once the buffer clears, server-reported counts are accepted normally.
  if (recentlyMarkedReadIds && typeof recentlyMarkedReadIds.has === 'function') {
    Object.keys(next).forEach((conversationId) => {
      if (recentlyMarkedReadIds.has(conversationId)) {
        const prevValue = previous && Number.isFinite(previous[conversationId])
          ? previous[conversationId]
          : 0;
        next[conversationId] = prevValue;
      }
    });
  }

  const prevKeys = Object.keys(previous || {});
  const nextKeys = Object.keys(next);
  if (prevKeys.length !== nextKeys.length) return next;
  for (const key of nextKeys) {
    if ((previous?.[key] || 0) !== next[key]) {
      return next;
    }
  }
  return previous || {};
}

export function shouldPlayDing({
  notificationSoundEnabled = true,
  conversationType,
  conversationId,
  selectedConversationId,
  previousUnread,
  nextUnread,
  now,
  lastDingAt,
  throttleMs = DING_THROTTLE_MS
}) {
  if (notificationSoundEnabled === false) return false;
  const normalizedType = String(conversationType || '').toUpperCase();
  if (normalizedType !== 'DM' && normalizedType !== 'CHANNEL') return false;
  if (!conversationId || conversationId === selectedConversationId) return false;
  if (!Number.isFinite(previousUnread) || !Number.isFinite(nextUnread)) return false;
  if (nextUnread <= previousUnread) return false;
  if (!Number.isFinite(now) || !Number.isFinite(lastDingAt)) return false;
  if (now - lastDingAt < throttleMs) return false;
  return true;
}

export function buildSelfIdentitySet({
  profileUserId,
  profileUsername,
  profileEmail,
  membershipUserIds = []
}) {
  const ids = new Set();
  [profileUserId, profileUsername, profileEmail].forEach((value) => {
    const normalized = normalizeIdentity(value);
    if (normalized) ids.add(normalized);
  });
  (membershipUserIds || []).forEach((value) => {
    const normalized = normalizeIdentity(value);
    if (normalized) ids.add(normalized);
  });
  return ids;
}

export function isSelfMessageEvent(message, selfIdentitySet) {
  const senderId = normalizeIdentity(message?.senderId);
  if (!senderId) return false;
  return Boolean(selfIdentitySet?.has(senderId));
}
